import { useMemo } from 'react'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'

import {
  RadarChart
} from 'echarts/charts'

import {
  GridComponent
} from 'echarts/components'

import { CanvasRenderer } from 'echarts/renderers'

import { SisetuResult } from 'interface/sisetu'
import { Box, Chip, ChipProps, Stack, Typography } from '@mui/material'
import { flatten, numberFormat } from 'shared/util'
import { useSearch } from 'features/search/context'
import { hiddenFlags } from 'features/search/util'

echarts.use([
  GridComponent,
  RadarChart,
  CanvasRenderer
])

const scoreToValue = (score: number | null | undefined, min: number, max: number) => {
  if (score === undefined || score === null) return null
  return Math.max(Math.min(((score - min) * 100) / (max - min), 100), 0)
}

type Vertex = {
  x: number
  y: number
}
type RaderAxisParam = {
  key: string
  label: string
  color: ChipProps["color"]
  min: number
  max: number
  radius?: number
  value?: number
  unit?: string
}
type RaderAxis = RaderAxisParam & {
  position: Vertex
  orientationX: 'left' | 'center' | 'right'
  orientationY: 'top' | 'middle' | 'bottom'
}
const getPolygonVertices = (
  width: number | undefined,
  height: number | undefined,
  margin: number,
  axisList: RaderAxisParam[]
): RaderAxis[] => {
  if (!width || !height || axisList.length < 3) return []
  const cx = width / 2
  const cy = height / 2
  const rh = (height / 2) - margin
  const mx = 5  // 中央寄せの閾値
  const my = 5
  const angleStep = (2 * Math.PI) / axisList.length
  const angleOffset = -Math.PI / 2; // -90度
  return axisList.map((axis, i) => {
    const currentAngle = i * angleStep + angleOffset
    const position: Vertex = {
      x: cx - rh * Math.cos(currentAngle),
      y: cy + rh * Math.sin(currentAngle)
    }
    const orientationX: RaderAxis["orientationX"] = (
      (Math.abs(position.x - cx) < mx) ? 'center' :
      (position.x < cx) ? 'left' : 'right'
    )
    const orientationY: RaderAxis["orientationY"] = (
      (Math.abs(position.y - cy) < my) ? 'middle' :
      (position.y < cy) ? 'top' : 'bottom'
    )
    return {
      ...axis,
      position,
      orientationX,
      orientationY
    }
  })
}

type SisetuScoreRadarProps = {
  sisetu: SisetuResult,
  width: number | undefined
  height: number | undefined
}
export const SisetuScoreRadar = ({
  sisetu,
  width,
  height
}: SisetuScoreRadarProps) => {
  const search = useSearch()
  const axisList = useMemo<RaderAxis[]>(
    () => {
      if (!search) return []

      const {
        hideResearch,
        hideDPC,
        hideCancer,
        hideJournal,
        hideSenmoni,
        hideTrial
      } = hiddenFlags(search)

      return getPolygonVertices(width, height, 30, flatten([
        {
          key: 'bedCount',
          label: '病床数',
          color: 'default',
          min: 0, max: 1000,
          value: sisetu.bedCount?.total ?? undefined,
          unit: '床'
        },
        hideResearch ? undefined : {
          key: 'research',
          label: '研究',
          color: 'research',
          min: 0, max: 160,
          radius: sisetu.score?.research?.deviation,
          value: sisetu.score?.research?.rank,
          unit: '位'
        },
        hideDPC ? undefined : {
          key: 'dpc',
          label: 'DPC',
          color: 'dpc',
          min: 0, max: 160,
          radius: sisetu.score?.dpc?.deviation,
          value: sisetu.score?.dpc?.rank,
          unit: '位'
        },
        hideCancer ? undefined : {
          key: 'cancer',
          label: 'がん',
          color: 'cancer',
          min: 0, max: 160,
          radius: sisetu.score?.cancer?.deviation,
          value: sisetu.score?.cancer?.rank,
          unit: '位'
        },
        hideSenmoni ? undefined : {
          key: 'senmoni',
          label: '専門医',
          color: 'senmoni',
          min: 0, max: 160,
          radius: sisetu.score?.senmoni?.deviation,
          value: sisetu.score?.senmoni?.rank,
          unit: '位'
        },
        hideTrial ? undefined : {
          key: 'trial',
          label: '治験',
          color: 'trial',
          min: 0, max: 160,
          radius: sisetu.score?.trial?.deviation,
          value: sisetu.score?.trial?.rank,
          unit: '位'
        },
        hideJournal ? undefined : {
          key: 'journal',
          label: '論文',
          color: 'journal',
          min: 0, max: 160,
          radius: sisetu.score?.journal?.deviation,
          value: sisetu.score?.journal?.rank,
          unit: '位'
        }
      ]))
    },
    [search, sisetu, width, height]
  )
  return (
    <Box position="relative">
      <Stack direction="row" justifyContent="center" alignItems="baseline" paddingX={1} spacing={1} flexWrap="wrap" useFlexGap sx={{
        position: "absolute",
        top: 0, left: 5
      }}>
        <Typography variant="body2" color='text.primary' component="span">総合</Typography>
        <Typography variant="h6" color='text.primary' component="span">{numberFormat(sisetu.score?.rank)}</Typography>
        <Typography variant="body2" color='text.primary' component="span">位</Typography>
      </Stack>

      {axisList.map(({ key, label, color, value, unit, position, orientationX, orientationY }) => (
        <Chip
          key={`raderlabel-${key}`}
          label={flatten([
            label,
            value ? numberFormat(value) : null,
            value ? unit : null
          ]).join(' ')}
          color={value ? color : undefined}
          disabled={value ? false : true}
          size="small"
          sx={{
            position: "absolute",
            top: position.y,
            left: position.x,
            background: (color === "default" || value === undefined) ? "transparent" : undefined,
            transform: `translate(${
              orientationX === 'center' ? '-50%' :
              orientationX === 'left' ? '-100%' : '0'
            }, ${
              orientationY === 'top' ? '-100%' :
              orientationY === 'middle' ? '-50%' : '0'
            })`,
            zIndex: 10
          }}
        />
      ))}

      <ReactEChartsCore
        echarts={echarts}
        option={{
          //animation: false,
          radar: axisList.length >= 3 ? {
            indicator: axisList.map(_ => ({ min: 0, max: 100 })),
            shape: 'circle',
            radius: 75
          } : undefined,
          series: axisList.length >= 3 ? [{
            name: 'SisetuScore',
            type: 'radar',
            data: [{
              value: axisList.map(({ min, max, radius, value }) => scoreToValue(radius ?? value, min, max)),
              name: 'result',
              areaStyle: {
                color: 'rgba(255, 228, 52, 0.8)'
              }
            }]
          }] : undefined,
          textStyle: {
            fontFamily: 'Noto Sans JP,sans-serif',
            color: '#CCC'
          }
        }}
        notMerge={true}
  //    lazyUpdate={true}
  //    theme={"theme_name"}
  //    onChartReady={onChartReadyCallback}
  //    onEvents={EventsDict}
        opts={{ renderer: 'canvas' }}
        style={{
          height
        }}
        theme="insite"
     />
    </Box>
  )
}